import { useContext, useEffect, useMemo, useState } from "react"
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    Stack,
    TextField,
    Typography
} from "@mui/material"
import { LoadingButton } from "@mui/lab"
import { DataGridPro } from "@mui/x-data-grid-pro"
import Editor from "@monaco-editor/react"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { format } from "date-fns"
import { parse, stringify } from "lossless-json"
import { SnackBarContext } from "../../snackBarProvider"

const columns = [
    {
        field: "date",
        headerName: "Date",
        width: 170,
        sortable: false,
        valueGetter: (value) => {
            if (!value) return "—"
            try {
                return format(new Date(value), "dd.MM.yyyy HH:mm:ss")
            } catch {
                return String(value)
            }
        }
    },
    {
        field: "reason",
        headerName: "Reason",
        flex: 1,
        minWidth: 140,
        sortable: false,
        valueGetter: (value) => value ?? "—"
    },
    {
        field: "syncID",
        headerName: "Sync ID",
        width: 160,
        sortable: false,
        valueGetter: (value) => value ?? "—"
    },
    {
        field: "clientStateID",
        headerName: "Client State ID",
        width: 160,
        sortable: false,
        valueGetter: (value) => value ?? "—"
    },
    {
        field: "clientVendorID",
        headerName: "Client Vendor ID",
        width: 160,
        sortable: false,
        valueGetter: (value) => value ?? "—"
    },
    {
        field: "clientSaveID",
        headerName: "Client Save",
        width: 110,
        sortable: false,
        type: "number"
    },
    {
        field: "serverStateID",
        headerName: "Server State ID",
        width: 160,
        sortable: false,
        valueGetter: (value) => value ?? "—"
    },
    {
        field: "serverVendorID",
        headerName: "Server Vendor ID",
        width: 160,
        sortable: false,
        valueGetter: (value) => value ?? "—"
    },
    {
        field: "serverSaveID",
        headerName: "Server Save",
        width: 110,
        sortable: false,
        type: "number"
    }
]

function formatStateJson(raw) {
    if (raw == null || raw === "") return ""
    try {
        return stringify(parse(raw), null, 2)
    } catch {
        return String(raw)
    }
}

function prepareStateBody(raw) {
    if (raw == null || raw === "") return ""
    try {
        return stringify(parse(raw))
    } catch {
        return String(raw)
    }
}

function MetaRow({ label, value }) {
    return (
        <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                {value ?? "—"}
            </Typography>
        </Stack>
    )
}

export default function SyncConflictsPage({
    game,
    useListQuery,
    useDetailQuery,
    createState,
    createStateResult
}) {
    const { addAlert } = useContext(SnackBarContext)

    const [stateId, setStateId] = useState("")
    const [vendorId, setVendorId] = useState("")
    const [appliedStateId, setAppliedStateId] = useState("")
    const [appliedVendorId, setAppliedVendorId] = useState("")
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 })
    const [selectedId, setSelectedId] = useState(null)

    const [clientStateText, setClientStateText] = useState("")
    const [serverStateText, setServerStateText] = useState("")

    const [applySide, setApplySide] = useState(null)
    const [applyUserId, setApplyUserId] = useState("")
    const [applyVendorId, setApplyVendorId] = useState("")
    const [confirmOpen, setConfirmOpen] = useState(false)

    const canSearch = !!appliedStateId || !!appliedVendorId

    const queryArgs = useMemo(() => ({
        stateId: appliedStateId || undefined,
        vendorId: appliedVendorId || undefined,
        page: paginationModel.page,
        limit: paginationModel.pageSize
    }), [appliedStateId, appliedVendorId, paginationModel])

    const { data, isLoading, isFetching, isError, error } = useListQuery(queryArgs, {
        skip: !canSearch
    })

    const {
        data: detail,
        isLoading: detailLoading,
        isFetching: detailFetching,
        isError: detailError
    } = useDetailQuery(selectedId, { skip: !selectedId })

    useEffect(() => {
        if (!detail) {
            setClientStateText("")
            setServerStateText("")
            return
        }
        setClientStateText(formatStateJson(detail.clientState))
        setServerStateText(formatStateJson(detail.serverState))
    }, [detail?.id])

    const rows = Array.isArray(data) ? data : []

    const handleSearch = () => {
        const nextStateId = stateId.trim()
        const nextVendorId = vendorId.trim()
        if (!nextStateId && !nextVendorId) {
            addAlert({ text: "Enter State ID and/or Vendor ID", type: "error" })
            return
        }
        setAppliedStateId(nextStateId)
        setAppliedVendorId(nextVendorId)
        setPaginationModel((m) => ({ ...m, page: 0 }))
        setSelectedId(null)
    }

    const handleClear = () => {
        setStateId("")
        setVendorId("")
        setAppliedStateId("")
        setAppliedVendorId("")
        setPaginationModel({ page: 0, pageSize: 25 })
        setSelectedId(null)
    }

    const handleFilterKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault()
            handleSearch()
        }
    }

    const openApplyDialog = (side) => {
        if (!detail) return
        if (side === "client") {
            setApplyUserId(detail.clientStateID || "")
            setApplyVendorId(detail.clientVendorID || detail.clientStateID || "")
        } else {
            setApplyUserId(detail.serverStateID || "")
            setApplyVendorId(detail.serverVendorID || detail.serverStateID || "")
        }
        setApplySide(side)
        setConfirmOpen(true)
    }

    const handleConfirmApply = async () => {
        setConfirmOpen(false)
        const userId = applyUserId.trim()
        if (!userId) {
            addAlert({ text: "User ID is required", type: "error" })
            return
        }
        const rawState = applySide === "client" ? detail?.clientState : detail?.serverState
        const body = prepareStateBody(rawState)
        if (!body) {
            addAlert({ text: "State is empty", type: "error" })
            return
        }

        const response = await createState({
            userId,
            vendorId: applyVendorId.trim() || userId,
            game,
            body,
            version: "v2"
        })

        if (response.error) {
            if (response.error.status === 403) {
                addAlert({ text: "This id is not in the whitelist", type: "error" })
            } else {
                addAlert({ text: "Something went wrong", type: "error" })
            }
        } else {
            addAlert({ text: "State applied", type: "success" })
        }
    }

    return (
        <>
            <Card sx={{mb:2, pb:0}}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Sync Conflicts
                    </Typography>

                    <Stack direction="row" spacing={2} sx={{  flexWrap: "wrap", alignItems: "center" }}>
                        <TextField
                            label="State ID"
                            size="small"
                            value={stateId}
                            onChange={(e) => setStateId(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                            sx={{ width: 280 }}
                        />
                        <TextField
                            label="Vendor ID"
                            size="small"
                            value={vendorId}
                            onChange={(e) => setVendorId(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                            sx={{ width: 280 }}
                        />
                        <Button variant="contained" size="small" onClick={handleSearch}>
                            Search
                        </Button>
                        <Button variant="outlined" size="small" onClick={handleClear}>
                            Clear
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            {!canSearch ? (
                <Alert severity="info">Enter State ID and/or Vendor ID and press Search</Alert>
            ) : isError ? (
                <Alert severity="error">
                    {error?.data?.message || error?.error || "Failed to load conflicts"}
                </Alert>
            ) : (
                <Box sx={{ width: "100%", height: 420, mb: 2 }}>
                    <DataGridPro
                        density="compact"
                        disableColumnMenu
                        disableColumnSorting
                        pagination
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[10, 25, 50]}
                        rowCount={-1}
                        loading={isLoading || isFetching}
                        columns={columns}
                        rows={rows}
                        getRowId={(row) => row.id}
                        onRowClick={(params) => setSelectedId(params.id)}
                        getRowClassName={(params) =>
                            params.id === selectedId ? "Mui-selected" : ""
                        }
                        sx={{
                            "& .MuiDataGrid-row": { cursor: "pointer" },
                            "& .MuiDataGrid-cell": { py: 1, alignItems: "center" }
                        }}
                    />
                </Box>
            )}

            {selectedId && (
                <Card>
                    <CardContent>
                        {(detailLoading || detailFetching) && !detail ? (
                            <Typography color="text.secondary">Loading conflict…</Typography>
                        ) : detailError ? (
                            <Alert severity="error">Failed to load conflict details</Alert>
                        ) : detail ? (
                            <>
                                <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
                                    <LoadingButton
                                        variant="contained"
                                        color="success"
                                        startIcon={<CheckCircleIcon />}
                                        loading={createStateResult?.isLoading && applySide === "client"}
                                        onClick={() => openApplyDialog("client")}
                                    >
                                        Apply client state
                                    </LoadingButton>
                                    <LoadingButton
                                        variant="contained"
                                        color="primary"
                                        startIcon={<CheckCircleIcon />}
                                        loading={createStateResult?.isLoading && applySide === "server"}
                                        onClick={() => openApplyDialog("server")}
                                    >
                                        Apply server state
                                    </LoadingButton>
                                </Stack>

                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Meta
                                        </Typography>
                                        <MetaRow label="ID" value={detail.id} />
                                        <MetaRow
                                            label="Date"
                                            value={
                                                detail.date
                                                    ? format(new Date(detail.date), "dd.MM.yyyy HH:mm:ss")
                                                    : null
                                            }
                                        />
                                        <MetaRow label="Reason" value={detail.reason} />
                                        <MetaRow label="Sync ID" value={detail.syncID} />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Client / Server IDs
                                        </Typography>
                                        <MetaRow label="Client State ID" value={detail.clientStateID} />
                                        <MetaRow label="Client Vendor ID" value={detail.clientVendorID} />
                                        <MetaRow label="Client Save ID" value={detail.clientSaveID} />
                                        <MetaRow label="Server State ID" value={detail.serverStateID} />
                                        <MetaRow label="Server Vendor ID" value={detail.serverVendorID} />
                                        <MetaRow label="Server Save ID" value={detail.serverSaveID} />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Client state
                                        </Typography>
                                        <Editor
                                            height="50vh"
                                            defaultLanguage="json"
                                            value={clientStateText}
                                            options={{ readOnly: true, fontFamily: "JetBrains Mono Variable" }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Server state
                                        </Typography>
                                        <Editor
                                            height="50vh"
                                            defaultLanguage="json"
                                            value={serverStateText}
                                            options={{ readOnly: true, fontFamily: "JetBrains Mono Variable" }}
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        ) : null}
                    </CardContent>
                </Card>
            )}

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Typography gutterBottom>
                        Apply <strong>{applySide}</strong> state
                    </Typography>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="User ID"
                            size="small"
                            required
                            fullWidth
                            value={applyUserId}
                            onChange={(e) => setApplyUserId(e.target.value)}
                        />
                        <TextField
                            label="Vendor ID"
                            size="small"
                            fullWidth
                            value={applyVendorId}
                            onChange={(e) => setApplyVendorId(e.target.value)}
                            helperText="Defaults to User ID if empty"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button  onClick={() => setConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={handleConfirmApply}
                        variant="outlined"
                        color="warning"
                        loading={createStateResult?.isLoading}
                    >
                        APPLY
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}
